import React from "react";
import { cn } from "../../utils/cn";

/**
 * @description 
 * Input component for collecting user data.
 * Follows shadcn/ui design principles with support for different states.
 * 
 * Key features:
 * - Full control via React.forwardRef
 * - Error state styling
 * - Disabled state handling
 * - Size variants
 * 
 * @dependencies
 * - cn: Utility for merging Tailwind classes
 * 
 * @example
 * <Input type="email" placeholder="Email" />
 * <Input type="password" placeholder="Password" hasError />
 * <Input type="text" disabled placeholder="Disabled" />
 */

const Input = React.forwardRef(
  ({ className, type, hasError, size = "default", ...props }, ref) => {
    const sizeClasses = {
      default: "h-10 px-4 py-2",
      sm: "h-8 px-3 py-1 text-sm",
      lg: "h-12 px-5 py-3 text-lg"
    };
    
    return (
      <input
        type={type}
        className={cn(
          "flex w-full rounded-md border border-[var(--border-subtle)] bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm text-[var(--text-primary)] shadow-sm transition-colors",
          "placeholder:text-[var(--text-tertiary)]",
          "focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent",
          "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-[var(--background-inset)]",
          hasError && "border-red-500 focus:ring-red-500",
          sizeClasses[size],
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Input.displayName = "Input";

export { Input }; 